/**
 * Arbora — GSAP ScrollTrigger Animations
 * Horizon Theme — Homepage
 *
 * HOW TO INSTALL:
 * 1. Go to Shopify Admin → Online Store → Themes → Edit code
 * 2. Open layout/theme.liquid
 * 3. Paste the GSAP CDN scripts just before </body>
 * 4. Open sections/main-index.liquid (or your homepage section file)
 * 5. Paste this entire <script> block at the bottom of that file
 *
 * CDN SCRIPTS TO ADD IN theme.liquid before </body>:
 * <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js"></script>
 * <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js"></script>
 * <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/SplitText.min.js"></script>
 */

document.addEventListener('DOMContentLoaded', () => {

  // ─── Register Plugins ───────────────────────────────────────────────────────
  gsap.registerPlugin(ScrollTrigger, SplitText);

  // ─── Respect Reduced Motion ──────────────────────────────────────────────────
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  // ─── 1. HERO SECTION — Text Split Letter Animation ──────────────────────────
  // Targets Horizon's hero heading and subtext
  const heroHeadings = document.querySelectorAll(
    '.banner__heading, .hero__heading, [class*="hero"] h1, [class*="banner"] h1, .section-header h1'
  );

  heroHeadings.forEach(heading => {
    const split = new SplitText(heading, { type: 'chars, words' });
    gsap.from(split.chars, {
      opacity: 0,
      y: 60,
      rotationX: -90,
      stagger: 0.03,
      duration: 0.8,
      ease: 'back.out(1.5)',
      scrollTrigger: {
        trigger: heading,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Hero subtext / paragraph fade-up
  const heroSubtext = document.querySelectorAll(
    '.banner__text, .hero__text, [class*="hero"] p, [class*="banner"] p'
  );
  gsap.from(heroSubtext, {
    opacity: 0,
    y: 40,
    duration: 1,
    ease: 'power3.out',
    stagger: 0.15,
    scrollTrigger: {
      trigger: heroSubtext[0],
      start: 'top 88%',
      toggleActions: 'play none none reverse',
    },
  });

  // ─── 2. PARALLAX DEPTH — Images scroll at different rates ────────────────────
  // Horizon uses .banner__media, .media, and image wrappers
  const parallaxImages = document.querySelectorAll(
    '.banner__media img, .banner__media .media, ' +
    '.featured-collection .media img, ' +
    '.rich-text__media img, ' +
    '.image-with-text__media img'
  );

  parallaxImages.forEach(img => {
    gsap.fromTo(img,
      { yPercent: -12 },
      {
        yPercent: 12,
        ease: 'none',
        scrollTrigger: {
          trigger: img.closest('section') || img.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5,
        },
      }
    );
  });

  // ─── 3. STAGGERED PRODUCT CARD REVEALS ───────────────────────────────────────
  // Horizon's product grids use .card, .product-card, .grid__item
  const productGrids = document.querySelectorAll(
    '.collection-list, .featured-collection__grid, ' +
    '[class*="product-grid"], [class*="collection-grid"]'
  );

  productGrids.forEach(grid => {
    const cards = grid.querySelectorAll(
      '.card-wrapper, .grid__item, [class*="product-card"], li'
    );

    gsap.from(cards, {
      opacity: 0,
      y: 80,
      scale: 0.93,
      stagger: {
        each: 0.1,
        from: 'start',
      },
      duration: 0.85,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: grid,
        start: 'top 80%',
        toggleActions: 'play none none none',
      },
    });
  });

  // ─── 4. PINNED SCROLL SECTION — Featured text/image lock ─────────────────────
  // Targets Horizon's image-with-text or rich-text sections
  // This pins the section while content animates in
  const pinnedSections = document.querySelectorAll(
    '.image-with-text, .rich-text, [class*="feature-row"]'
  );

  pinnedSections.forEach(section => {
    const media = section.querySelector('.image-with-text__media, .media, img');
    const content = section.querySelector(
      '.image-with-text__content, .rich-text__blocks, [class*="text-content"]'
    );

    if (!media || !content) return;

    // Media slides in from left, content from right, while section is pinned
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 60%',
        end: 'bottom 40%',
        scrub: 1,
        pin: false, // set to true if you want hard pin; can feel heavy on mobile
      },
    });

    tl.from(media, { x: -60, opacity: 0, duration: 1, ease: 'power2.out' }, 0)
      .from(content, { x: 60, opacity: 0, duration: 1, ease: 'power2.out' }, 0.1);
  });

  // ─── 5. SECTION HEADINGS — Slide + fade across all sections ──────────────────
  const sectionHeadings = document.querySelectorAll(
    '.collection-list-details__title, ' +
    '.featured-collection__title, ' +
    '.rich-text__heading, ' +
    'h2.title, h2[class*="heading"], .section-header h2'
  );

  sectionHeadings.forEach(h => {
    gsap.from(h, {
      opacity: 0,
      y: 50,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: h,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // ─── 6. FOOTER — Staggered column reveal ─────────────────────────────────────
  const footerCols = document.querySelectorAll(
    'footer .footer__column, footer .footer__blocks > *, footer .grid > *'
  );

  gsap.from(footerCols, {
    opacity: 0,
    y: 40,
    stagger: 0.12,
    duration: 0.8,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: 'footer',
      start: 'top 90%',
      toggleActions: 'play none none none',
    },
  });

  // ─── 7. ANNOUNCEMENT BAR / MARQUEE — subtle pulse ────────────────────────────
  const announcementBar = document.querySelector(
    '.announcement-bar, [class*="announcement"]'
  );
  if (announcementBar) {
    gsap.from(announcementBar, {
      opacity: 0,
      y: -20,
      duration: 0.6,
      ease: 'power2.out',
      delay: 0.3,
    });
  }

  // ─── 8. MOBILE OPTIMIZATION ──────────────────────────────────────────────────
  // On mobile, reduce parallax intensity and disable heavy pins
  ScrollTrigger.matchMedia({
    '(max-width: 749px)': function () {
      // Refresh all triggers with reduced offset for smaller screens
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars.scrub) {
          trigger.vars.scrub = 0.5; // faster scrub on mobile
        }
      });
    },
  });

  // ─── Refresh on fonts/images load ───────────────────────────────────────────
  window.addEventListener('load', () => ScrollTrigger.refresh());

});